import type { QueryKey } from '@tanstack/react-query';

import { useQuery } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { ReadDependencyLicenseAPIResponse } from '../../../../shared/types/licenses';

//  We can keep this data around for a bit longer since license data basically never changes.
const STALE_TIME = 1000 * 60 * 30; // 30 minutes

async function getRevisionLicense(revisionLocator: string, revisionLicenseId: number) {
  return get<ReadDependencyLicenseAPIResponse>(`/api/revisions/${nuc(revisionLocator)}/licenses/${revisionLicenseId}`);
}

export const getQueryKey = (revisionLocator: string, revisionLicenseId: number): QueryKey => ['revision', revisionLocator, 'licenses', revisionLicenseId];

/**
 * Hook which will fetch data about a given license.
 * Caches the license data for 10 minutes, since it's unlikely to ever change.
 *
 * @param {string} revisionLocator The locator of the revision to fetch.
 * @param {number} revisionLicenseId The ID of the license to fetch.
 * @param {boolean} shouldFetch Whether to fetch the license data or not. Defaults to true.
 */
export default function useRevisionLicense(revisionLocator: string, revisionLicenseId: number, shouldFetch = true) {
  return useQuery<ReadDependencyLicenseAPIResponse>({
    queryKey: getQueryKey(revisionLocator, revisionLicenseId),
    queryFn: () => getRevisionLicense(revisionLocator, revisionLicenseId),
    staleTime: STALE_TIME,
    enabled: shouldFetch && !!revisionLocator && !!revisionLicenseId,
  });
}
